import React from 'react'
import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  useTheme
} from '@mui/material'
import { LockOpenIcon, logo, MobileIcon } from 'assets/icons'
import Analytics, { Events } from 'analytics/Analytics'
import Database from 'services/db/Database'

import { processLogout } from 'components/providers/AuthProvider'
import { doGetChanges } from 'components/providers/GetChangesProvider'
import { useNavigate } from 'react-router-dom'
import { LocalStorageKey } from 'types/services/localStorage'
import { APP_LINK, isMobileDevice } from 'utils/constants'
import { extractDeepLinkCode } from 'utils/deeplink'
import { getMobileOS, appStoreLinkGroupcal } from 'utils/device'
import { isTestEnv } from 'utils/env'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { GroupDoc } from 'types/api/changes'
import { Model } from 'types/model'
import { t } from 'i18next'
import i18n from 'i18n'

interface MobileHeaderProps {
  group?: Model<GroupDoc> | GroupDoc | undefined
}

const GuestHeader: React.FC<MobileHeaderProps> = ({ group }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const goToLogin = () => {
    Analytics.getInstance().sendEvent(Events.GEUST_MODE_OPEN_FULL_VERSION)
    const inviteCode = extractDeepLinkCode(group?.PrivateLinkUrl ?? '') // set this dynamically

    Database.clear()
      .then(() => {
        if (getMobileOS() === 'android') {
          if (inviteCode) {
            const playStoreUrl = `https://play.google.com/store/apps/details?id=app.groupcal.www${isTestEnv() ? '.debug' : ''}&referrer=invite_code%3D${encodeURIComponent(inviteCode)}`
            window.open(playStoreUrl, '_self')
          } else {
            // Open new page in the same tab with mobile link
            window.location.href = `${APP_LINK}/${LocalStorage.get(
              LocalStorageKey.MOBILE_LINK
            )}`
          }
        } else if (getMobileOS() === 'ios') {
          navigator.clipboard.writeText(`Deeplink=${inviteCode}`)
          window.open(appStoreLinkGroupcal, '_blank')

          if (!inviteCode) {
            window.location.href = `${APP_LINK}/${LocalStorage.get(
              LocalStorageKey.MOBILE_LINK
            )}`
          }
        } else {
          navigate('/')
          LocalStorage.remove(LocalStorageKey.LAST_UPDATE)
          LocalStorage.remove(LocalStorageKey.CLEAN_GET_CHANGES_DONE)
          processLogout().then(() => {
            doGetChanges(null, undefined, null)
          })
        }
      })
      .catch((error) => {
        Logger.red('GuestMode', 'Error clearing database', error)
      })
  }

  const openMainPage = () => {
    window.open('https://app.groupcal.app/')
  }

  return (
    <Box
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      width={'100%'}
      paddingBottom={isMobileDevice() ? '16px' : '24px'}
      sx={{
        backgroundColor: theme.palette.card.primary
      }}
    >
      <Grid
        container
        width={'100%'}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        position={'relative'}
      >
        {!isMobileDevice() && (
          <Grid
            item
            xs={'auto'}
            sx={{
              position: 'absolute',
              left: i18n.dir() === 'ltr' ? 0 : undefined,
              right: i18n.dir() === 'rtl' ? 0 : undefined,
              backgroundColor: theme.palette.card.primary,
              paddingLeft: i18n.dir() === 'ltr' ? '16px' : undefined,
              paddingRight: i18n.dir() === 'rtl' ? '16px' : undefined,
              zIndex: 1
            }}
            id={'main-page-link'}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '11px',
                flexShrink: 0
              }}
            >
              <IconButton onClick={openMainPage}>
                <img
                  src={logo}
                  style={{ width: 36, height: 36, cursor: 'pointer' }}
                />
              </IconButton>
              <span onClick={openMainPage} style={{ cursor: 'pointer' }}>
                <Typography fontFamily={'Montserrat'} fontSize={'24px'}>
                  GroupCal
                </Typography>
              </span>
            </Box>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={'auto'}
          display={'flex'}
          justifyContent={'center'}
          id={'go-login-box'}
          sx={{
            zIndex: 2,
            position: 'relative'
          }}
        >
          <Box
            id="joinGroupButton"
            onClick={goToLogin}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobileDevice() ? 2 : 4,
              padding: '8px',
              width: isMobileDevice() ? undefined : 'fit-content',
              border: '1px solid var(--color-accent)',
              backgroundColor:
                'color-mix(in srgb, var(--color-accent) 20%, var(--color-surface-card))',
              borderRadius: '12px'
            }}
          >
            {isMobileDevice() ? (
              <MobileIcon />
            ) : (
              <LockOpenIcon width={'60px'} height={'60px'} />
            )}

            {isMobileDevice() ? (
              <Typography
                sx={{
                  flex: 1,
                  fontSize: '15px',
                  fontFamily: 'NotoSans',

                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  lineHeight: '22px'
                }}
              >
                {t('installGroupCal')}
              </Typography>
            ) : (
              <Box
                display={'flex'}
                width={'100%'}
                flexDirection={'column'}
                gap={2}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '20px',

                    fontFamily: 'NotoSans',

                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    lineHeight: '24px'
                  }}
                >
                  {t('unlockFull')}
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '16px',

                    fontFamily: 'NotoSans',

                    fontWeight: 400,
                    color: theme.palette.text.primary,
                    lineHeight: '22px'
                  }}
                >
                  {t('joinTheCalendar')}
                </Typography>
              </Box>
            )}
            <Button
              sx={{
                backgroundColor: '#EA5455',
                color: 'white',
                padding: isMobileDevice() ? '4px 16px' : '12px 24px',
                borderRadius: '24px',
                fontSize: isMobileDevice() ? '16px' : '20px',
                lineHeight: '22px',
                fontFamily: 'NotoSans',
                fontWeight: isMobileDevice() ? 500 : 400,
                textTransform: 'none',
                minWidth: 'fit-content',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: '#D63A3B'
                }
              }}
            >
              {isMobileDevice() ? t('get') : t('joinCalnedar')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default GuestHeader
